/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useRef } from 'react';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { ReactSelectInput, TextInput } from '@hivebrite/design-system/Inputs';
import { Label } from '@hivebrite/design-system/Label';
import { usePrevious } from '@hivebrite/react-hooks';

import InputLocation from 'hb-react/frontoffice/components/inputs/InputLocation';
import { ReactSelectSkills } from 'hb-react/shared/components/DesignSystem/Inputs/ReactSelect/Async';

const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

export const makeFilters = ({
  mentorApplicationConfig,
  customAttributesList,
}) => {
  const filters = [
    mentorApplicationConfig?.mentorshipsKeywordDisplayInSearchFilters && {
      id: 'who',
      component: ({ id, value, onChange }) => (
        <TextInput
          icon={SearchIcon}
          clearable
          id={id}
          name={id}
          label={I18n.t('web.companies.search_keyword_label')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={__('Type name or keyword')}
          value={value ?? ''}
        />
      ),
    },
    mentorApplicationConfig?.mentorshipsLocationDisplayInSearchFilters && {
      id: 'location',
      component: ({ id, value, onChange }) => {
        const componentRef = useRef();

        const prevValue = usePrevious(value);

        useEffect(() => {
          const hasValue = (val) => val?.location;

          if (hasValue(prevValue) && !hasValue(value)) {
            componentRef.current?.resetLocationState();
          }
        }, [prevValue, value]);

        return (
          <>
            <Label id={id}>{s__('Mentorships|Location')}</Label>
            <InputLocation
              inputName="address"
              placeholder={__('Type and select a location')}
              onSelectLocation={(value) => {
                if (value?.location && value?.location?.trim() !== '') {
                  onChange(value);
                }
              }}
              componentRef={(ref) => (componentRef.current = ref)}
            />
          </>
        );
      },
    },
    mentorApplicationConfig?.mentorshipsSkillsDisplayInSearchFilters && {
      id: 'skill_ids',
      component: ({ id, value, onChange }) => (
        <ReactSelectSkills
          id={id}
          name={id}
          placeholder={s__('Mentorships|Skills')}
          label={s__('Mentorships|Search by skills')}
          onChange={onChange}
          value={value ?? null}
          isMulti
          isSearchable
        />
      ),
    },
  ];

  const attributes = customAttributesList.filter(
    (attribute) =>
      attribute.attribute_type === 'select' &&
      attribute.display_in_search_filters,
  );

  attributes.forEach((attribute) => {
    filters.push({
      // Using [] inside a search params key causes issues
      // when parsing the query string so we're using () instead.
      // We do need to convert () back to [] before sending the
      // request to the backend.
      id: `customizable_attributes(${attribute.name})`,
      component: ({ id, value, onChange }) => (
        <ReactSelectInput
          id={id}
          name={id}
          placeholder={attribute.placeholder}
          label={attribute.display_name}
          options={attribute.options.map((option) => ({
            label: option,
            value: option,
          }))}
          onChange={onChange}
          value={value ?? null}
          isMulti
          isSearchable
        />
      ),
    });
  });

  return filters.filter(Boolean);
};
